import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { adminWsUrl, api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PendingUsersContext = createContext({ pending: 0, version: 0, refresh: () => {} });

export const usePendingUsers = () => useContext(PendingUsersContext);

/** Shared pending-approval count: live via the admin websocket, refreshable on demand. */
export function PendingUsersProvider({ children }) {
  const { capabilities } = useAuth();
  const isAdmin = Boolean(capabilities?.is_admin);
  const [pending, setPending] = useState(0);
  const [version, setVersion] = useState(0);
  const socketRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/admin/pending-count");
      setPending(data.pending);
    } catch (error) {
      console.debug("pending count unavailable", error);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) {
      setPending(0);
      return undefined;
    }
    refresh();
    let closed = false;
    let retry;
    const connect = () => {
      const socket = new WebSocket(adminWsUrl());
      socketRef.current = socket;
      socket.onmessage = (event) => {
        try {
          if (JSON.parse(event.data).event === "user_registered") {
            refresh();
            setVersion((v) => v + 1);
          }
        } catch (error) {
          console.debug("admin socket frame ignored", error);
        }
      };
      socket.onclose = () => {
        if (!closed) retry = setTimeout(connect, 5000);
      };
    };
    connect();

    return () => {
      closed = true;
      clearTimeout(retry);
      const socket = socketRef.current;
      if (!socket) return;
      if (socket.readyState === WebSocket.CONNECTING)
        socket.addEventListener("open", () => socket.close());
      else socket.close();
    };
  }, [isAdmin, refresh]);

  const value = useMemo(
    () => ({ pending, version, refresh }),
    [pending, version, refresh]
  );

  return <PendingUsersContext.Provider value={value}>{children}</PendingUsersContext.Provider>;
}
